import React from 'react';
import DayTabs from '../DayTabs';
import DTEControls from '../DTEControls';
import ScopeGateBanner from '../ScopeGateBanner';
import { useDteOverlay } from '../useDteOverlay';
import StrategyEditLauncher from '../StrategyEditLauncher';
import SchedulePreviewModal from '../SchedulePreviewModal';
import ProHeroBanner from './ProHeroBanner';
import ProScheduleTable from './ProScheduleTable';
import ProBasketModal from './ProBasketModal';
import { StrategyPositionsModalV2 } from '../../positions/StrategyPositionsModalV2';
import ConfirmTerminateDialog from '../../positions/ConfirmTerminateDialog';
import type { ScheduleModalContext } from '../../positions/StrategyPositionsModal';
import { scheduleToPositionRow } from '../../../utils/scheduleToPositionRow';
import {
  computeFillProgress,
  fillProgressVariant,
  effectiveSlPending,
} from '../../../utils/scheduleSummary';
import { computeStrategyRunState } from '../../../utils/scheduleChip';
import {
  applyScheduleFilters,
  collectBasketOptions,
  collectExpiryOptions,
  collectProductOptions,
  collectTradingTypeOptions,
  collectUnderlyingOptions,
  computeStatusCounts,
  computeStateCounts,
} from '../../../utils/scheduleFilters';
import { usePersistedScheduleFilters } from '../../../hooks/usePersistedScheduleFilters';
import { computeBasketTotals } from '../../../utils/basketScheduleTotals';
import { computeAnomalies } from '../../../utils/scheduleAnomalies';
import SharedToolbar from '../SharedToolbar';
import AnomalyBanner from '../AnomalyBanner';
import type { ScheduleDataBundle } from '../useScheduleData';

interface ProScheduleViewProps {
  data: ScheduleDataBundle;
}

/**
 * Pro view — the redesigned Schedule page. Same data as Classic (via the
 * shared useScheduleData hook); only layout + chips + modals differ.
 */
const ProScheduleView: React.FC<ProScheduleViewProps> = ({ data }) => {
  const {
    selectedDate, setSelectedDate,
    pageSearchQuery, setPageSearchQuery,
    payload, strategies, totals, loading, refreshing, error,
    isToday, isPast,
    isBrokerScope, canAct,
    setOpenedStrategyKey, setOpenedBasketId, setEditTarget,
    openedBasketId, editTarget,
    modalSummary, modalRows, openedStrategyKey,
    detailLoading,
    retryingId, exitingId, bulkActionPending, terminatingId,
    fetchSummary, fetchDetail,
    retryPosition, exitPosition,
    retryPositionsBulk, exitStrategyBulk, exitBasketBulk, retryBasketRejections,
    exitAllBulk,
    askTerminatePosition, askTerminateStrategy, askTerminateBasket, askTerminateAll,
    terminateRequest, terminateScopeLabel, closeTerminate, onTerminateConfirm,
    openStrategyFromBasket, closeStrategyModal, backToBasket,
    confirmDialog,
    currentIstHms,
  } = data;

  // Page-level search query feeds the filter; the local alias keeps the
  // existing `searchQuery` references downstream unchanged.
  const searchQuery = pageSearchQuery;
  const setSearchQuery = setPageSearchQuery;
  const [columnFilters, setColumnFilters] = usePersistedScheduleFilters('schedule:pro:filters');

  const filteredStrategies = React.useMemo(
    () => applyScheduleFilters(strategies, searchQuery, columnFilters),
    [strategies, searchQuery, columnFilters],
  );
  // Option lists for every header popover — built from the unfiltered list
  // so each popover shows every value the user could pick.
  const filterOptions = React.useMemo(() => ({
    baskets:      collectBasketOptions(strategies),
    underlyings:  collectUnderlyingOptions(strategies),
    expiries:     collectExpiryOptions(strategies),
    products:     collectProductOptions(strategies),
    tradingTypes: collectTradingTypeOptions(strategies),
  }), [strategies]);
  // Status counts reflect rows that pass every OTHER column filter.
  const statusCounts = React.useMemo(
    () => computeStatusCounts(
      applyScheduleFilters(strategies, searchQuery, { ...columnFilters, status: new Set() }),
    ),
    [strategies, searchQuery, columnFilters],
  );
  // State counts reflect rows that pass every OTHER column filter — including
  // the Health filter — so the State popover badges stay independent.
  const stateCounts = React.useMemo(
    () => computeStateCounts(
      applyScheduleFilters(strategies, searchQuery, { ...columnFilters, state: new Set() }),
    ),
    [strategies, searchQuery, columnFilters],
  );
  const filteredTotals = React.useMemo(
    () => (totals ? computeBasketTotals(filteredStrategies) : null),
    [filteredStrategies, totals],
  );

  // Exit All is enabled when ANY visible strategy has open positions or
  // pending entries — counted against the filtered set so the button caption
  // matches the rows the user currently sees.
  const exitableCount = React.useMemo(
    () => filteredStrategies.reduce(
      (n, s) => n + s.open_count + s.entry_counts.pending,
      0,
    ),
    [filteredStrategies],
  );
  const countLabel = `${filteredStrategies.length} ${
    filteredStrategies.length === 1 ? 'Strategy' : 'Strategies'
  }`;

  // Anomaly chips driven by UNFILTERED day totals so issues hidden by
  // an active filter still surface in the banner. Click → replace
  // Status column-filter (other dimensions preserved).
  const anomalies = React.useMemo(
    () => computeAnomalies(totals, { currentIstHms, isToday }),
    [totals, currentIstHms, isToday],
  );
  const handleAnomalyClick = React.useCallback(
    (statusFilter: import('../../../utils/scheduleFilters').ScheduleStatusFilter[]) => {
      setColumnFilters((prev) => ({ ...prev, status: new Set(statusFilter) }));
    },
    [setColumnFilters],
  );

  const dte = useDteOverlay();

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0 space-y-2">
            <DTEControls value={dte.prefs} onChange={dte.setPrefs} />
            <DayTabs
              selectedDate={selectedDate}
              onChange={setSelectedDate}
              dteByDate={dte.dteByDate}
              holidays={dte.holidays}
              onDatesChange={dte.onDatesChange}
            />
          </div>
        </div>

        {/* Hero */}
        {filteredTotals && (
          <ProHeroBanner
            totals={filteredTotals}
            selectedDate={selectedDate}
            isToday={isToday}
            dayHasActivity={strategies.length > 0}
          />
        )}

        {/* Anomaly banner — only renders when something needs attention. */}
        <AnomalyBanner anomalies={anomalies} onChipClick={handleAnomalyClick} />

        <ScopeGateBanner isToday={isToday} isBrokerScope={isBrokerScope} />

        {/* Toolbar — sits between hero banner and table so it reads as a
            row of table-scope controls rather than a page-level chrome bar. */}
        <SharedToolbar
          countLabel={countLabel}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          currentIstHms={currentIstHms}
          onExitAll={canAct ? exitAllBulk : undefined}
          exitableCount={exitableCount}
          exitAllPending={bulkActionPending}
          onTerminateAll={canAct ? askTerminateAll : undefined}
          isToday={isToday}
        />

        {/* Table */}
        <ProScheduleTable
          strategies={filteredStrategies}
          totalStrategyCount={strategies.length}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          filterOptions={filterOptions}
          statusCounts={statusCounts}
          stateCounts={stateCounts}
          selectedDate={selectedDate}
          isToday={isToday}
          isPast={isPast}
          isTradingDay={payload?.is_trading_day ?? true}
          currentIstHms={currentIstHms}
          loading={loading}
          refreshing={refreshing}
          error={error}
          onOpenStrategy={setOpenedStrategyKey}
          onOpenBasket={setOpenedBasketId}
          onEditStrategy={(strategyId, basketId) => setEditTarget({ strategyId, basketId })}
          slPendingCount={effectiveSlPending(filteredTotals)}
        />
      </div>

      {/* Strategy edit launcher */}
      {editTarget && (
        <StrategyEditLauncher
          strategyId={editTarget.strategyId}
          basketId={editTarget.basketId}
          onClose={() => setEditTarget(null)}
          onUpdated={() => { fetchSummary(); }}
        />
      )}

      {/* Basket modal */}
      {openedBasketId && (() => {
        const basketSummaries = strategies.filter((s) => s.basket_id === openedBasketId);
        if (basketSummaries.length === 0) return null;
        const basketName = basketSummaries[0].basket_name;
        return (
          <ProBasketModal
            basketId={openedBasketId}
            basketName={basketName}
            summaries={basketSummaries}
            onClose={() => setOpenedBasketId(null)}
            onOpenStrategy={openStrategyFromBasket}
            onExitBasket={canAct ? exitBasketBulk : undefined}
            onTerminateBasket={canAct ? askTerminateBasket : undefined}
            onRetryBasket={canAct ? retryBasketRejections : undefined}
            actionPending={bulkActionPending}
            onRefresh={fetchSummary}
            refreshing={refreshing}
          />
        );
      })()}

      {/* Strategy modal — V2 redesign (Pro view always shows the new layout) */}
      {openedStrategyKey && modalSummary && (
        modalSummary.has_positions ? (
          (() => {
            const fp = computeFillProgress(modalRows);
            const scheduleCtx: ScheduleModalContext = {
              scheduledEntryTime: modalSummary.scheduled_entry_time,
              scheduledExitTime:  modalSummary.scheduled_exit_time,
              scheduledExitDate:  modalSummary.scheduled_exit_date,
              linkedEntryDate:    modalSummary.linked_entry_date,
              tradingType:        modalSummary.trading_type || '',
              intradayExitMode:   modalSummary.intraday_exit_mode || '',
              underlying:         modalSummary.underlying,
              expiryType:         modalSummary.expiry_type,
              productType:        modalSummary.product_type || '',
              viewedDate:         selectedDate,
              nowIstHms:          currentIstHms,
              brokerList: modalSummary.brokers.length === 0
                ? null
                : modalSummary.brokers.length <= 3
                  ? modalSummary.brokers.join(' · ')
                  : `${modalSummary.brokers.slice(0, 3).join(' · ')} · + ${modalSummary.brokers.length - 3} more`,
              fillProgress: { ...fp, variant: fillProgressVariant(fp) },
              runState: (() => {
                const chip = computeStrategyRunState(
                  modalSummary,
                  { isToday, isPast, nowIstHms: currentIstHms },
                );
                return { label: chip.label, variant: chip.variant };
              })(),
            };
            return (
              <StrategyPositionsModalV2
                basketName={modalSummary.basket_name}
                strategyName={modalSummary.strategy_name}
                positions={modalRows.map(scheduleToPositionRow)}
                scheduleContext={scheduleCtx}
                onClose={closeStrategyModal}
                onBack={backToBasket}
                backLabel="Back to basket"
                onRefresh={() => fetchDetail(openedStrategyKey!, true)}
                refreshing={detailLoading === openedStrategyKey}
                onExit={canAct ? (p) => exitPosition(p.positionId) : undefined}
                onRetry={canAct ? (p) => retryPosition(p.positionId) : undefined}
                onTerminate={canAct ? (p) => askTerminatePosition(p.positionId) : undefined}
                onTerminateAll={canAct ? () => askTerminateStrategy(modalSummary.strategy_id) : undefined}
                onExitAll={canAct
                  ? (_ids) => exitStrategyBulk(modalSummary.strategy_id)
                  : undefined}
                onRetryAll={canAct ? retryPositionsBulk : undefined}
                exitingId={exitingId}
                retryingId={retryingId}
                terminatingId={terminatingId}
                actionPending={bulkActionPending}
              />
            );
          })()
        ) : (
          <SchedulePreviewModal
            basketName={modalSummary.basket_name}
            strategyName={modalSummary.strategy_name}
            rows={modalRows}
            isToday={isToday}
            isPast={isPast}
            currentIstHms={currentIstHms}
            onClose={closeStrategyModal}
          />
        )
      )}

      {confirmDialog}
      <ConfirmTerminateDialog
        isOpen={!!terminateRequest}
        request={terminateRequest}
        scopeLabel={terminateScopeLabel}
        onClose={closeTerminate}
        onConfirm={onTerminateConfirm}
      />
    </>
  );
};

export default ProScheduleView;
